import React, { useMemo } from 'react';

import { faEye } from '@fortawesome/pro-regular-svg-icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ExamGridModel } from 'models/ExamGridModel';
import { FileModel } from 'models/FileModel';

import { Button, ButtonLooks, ButtonVariants, Icon } from 'core/ui';

import { DocumentCategories } from 'features/exam/constants';

import { CATEGORY_COLORS, getCategoryKeyById, sortFilesByDate } from '../services/file-category-helper';
import { Category } from './PatientFiles.types';
import { FileThumbnailDisplay } from './PatientFilesHelperComponents';

// -----------------------------------
// Styled components
// -----------------------------------
const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 24px;
  width: 100%;

  /* Single column on mobile */
  @media (max-width: 768px) {
    /* Use same breakpoint */
    grid-template-columns: 1fr;
    gap: 16px; /* Optionally reduce gap */
  }
`;

const FileCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
  margin-bottom: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
`;

const CategoryTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const StatusTag = styled.span<{ hasFiles: boolean }>`
  display: inline-block;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${({ hasFiles }) => (hasFiles ? '#d1e7dd' : '#f8d7da')};
  color: ${({ hasFiles }) => (hasFiles ? '#0f5132' : '#842029')};
  text-transform: uppercase;
`;

const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const FileThumbnail = styled.div`
  position: relative;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  margin-bottom: 16px;
  overflow: hidden;
  border-radius: 4px;
  transition: transform 0.2s ease-in-out;
  cursor: pointer;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
  }

  .placeholder-icon {
    font-size: 60px;
    color: #ccc;
  }

  ${FileCard}:hover & {
    transform: scale(1.05);
  }
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.3) 100%);
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 8px;
  box-sizing: border-box;

  ${FileCard}:hover & {
    opacity: 1;
  }
`;

const ViewIconButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  svg {
    font-size: 16px;
  }
`;

const FileInfo = styled.div`
  margin-top: auto;
`;

const FileText = styled.p`
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateText = styled.small`
  font-size: 0.8rem;
  color: #777;
`;

const LatestLabel = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  color: #198754;
  font-size: 0.85rem;
`;

const NoResultsMessage = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 24px;
`;

// -----------------------------------
// Helper Components (Inline for now)
// -----------------------------------

// -----------------------------------
// Category Card Component
// -----------------------------------
interface CategoryCardProps {
  category: Category;
  onFileSelect: (file: FileModel, category: Category) => void;
  onViewFile: (file: FileModel, category: Category) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, onFileSelect, onViewFile }) => {
  const sortedFiles = category.files;
  const mostRecentFile = sortedFiles[0] || null;
  const hasFiles = sortedFiles.length > 0;

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mostRecentFile) {
      onFileSelect(mostRecentFile, category);
    }
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mostRecentFile) {
      onViewFile(mostRecentFile, category);
    }
  };

  return (
    <FileCard key={category.key}>
      <CardHeader>
        <CategoryTitle>{category.displayName}</CategoryTitle>
        <StatusTag hasFiles={hasFiles}>{hasFiles ? 'Available' : 'Missing'}</StatusTag>
      </CardHeader>
      <CardContent>
        <FileThumbnail onClick={handlePreviewClick}>
          <FileThumbnailDisplay file={mostRecentFile} viewType="list" />
          {mostRecentFile && (
            <ThumbnailOverlay>
              <ViewIconButton onClick={handleViewClick} title="View Full File">
                <Icon icon={faEye} />
              </ViewIconButton>
            </ThumbnailOverlay>
          )}
        </FileThumbnail>

        {mostRecentFile && <LatestLabel>Latest Upload</LatestLabel>}

        <FileInfo>
          {mostRecentFile ? (
            <>
              <FileText title={mostRecentFile.fileName ?? 'No filename'}>{mostRecentFile.fileName ?? 'No filename'}</FileText>
              <DateText>Date: {mostRecentFile.created ? dayjs(mostRecentFile.created).format('MM/DD/YYYY') : 'No date'}</DateText>
            </>
          ) : (
            <FileText>No {category.displayName.toLowerCase()} files available.</FileText>
          )}
        </FileInfo>
      </CardContent>
    </FileCard>
  );
};

// -----------------------------------
// Props Interface
// -----------------------------------
interface PatientFilesListViewProps {
  files: FileModel[];
  gridData: ExamGridModel[];
  isLoading: boolean;
  onFileSelect: (file: FileModel, category: Category) => void;
  onViewFile: (file: FileModel, category: Category) => void;
}

// -----------------------------------
// Component
// -----------------------------------
export const PatientFilesListView: React.FC<PatientFilesListViewProps> = ({ files, gridData, isLoading, onFileSelect, onViewFile }) => {
  // Group files by category key
  const categories = useMemo<Category[]>(() => {
    const categoryMap = new Map<string, Category>();
    files.forEach((file) => {
      const categoryKey = getCategoryKeyById(file.categoryId ?? null) ?? 'UNCATEGORIZED';
      if (!categoryMap.has(categoryKey)) {
        const typedKey = categoryKey as keyof typeof DocumentCategories;
        const catInfo = categoryKey === 'UNCATEGORIZED' ? { name: 'Uncategorized' } : DocumentCategories[typedKey];
        categoryMap.set(categoryKey, {
          key: categoryKey,
          displayName: catInfo?.name ?? categoryKey,
          color: CATEGORY_COLORS[categoryKey] || '#ccc',
          files: [],
        });
      }
      categoryMap.get(categoryKey)!.files.push(file);
    });

    // Sort files within each category
    categoryMap.forEach((category) => {
      category.files = sortFilesByDate(category.files);
    });

    return Array.from(categoryMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [files]);

  if (isLoading) {
    return <NoResultsMessage>Loading files...</NoResultsMessage>;
  }

  if (categories.length === 0) {
    return <NoResultsMessage>No files found matching the current criteria.</NoResultsMessage>;
  }

  return (
    <FilesGrid>
      {categories.map((category) => (
        <CategoryCard key={category.key} category={category} onFileSelect={onFileSelect} onViewFile={onViewFile} />
      ))}
    </FilesGrid>
  );
};
